import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Button,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Typography,
    Grid,
    FormControlLabel,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { Add, Delete, AutoAwesome, FindInPage, Warning } from '@mui/icons-material';
import LevelSelector from '../shared/LevelSelector';
import { userRequest } from '../../requestMethods';
import TranslationMonitoringDialog from '../shared/TranslationMonitoringDialog';

const ShortStoryForm = ({ story, languages = [], onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
        title: '',
        language: '',
        level: '',
        sourceTitle: '',
        sourceUrl: '',
        thumbUrl: '',
        tags: [],
        author: '',
        textContent: '',
        isReadyToServe: false
    });

    const [tagInput, setTagInput] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [monitoringDialog, setMonitoringDialog] = useState({ open: false, loading: false, data: null });
    const [readyToServeDialog, setReadyToServeDialog] = useState({ open: false, hasChecked: false });
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (story) {
            setFormData({
                title: story.title || '',
                language: story.language?._id || '',
                level: story.level?._id || '',
                sourceTitle: story.sourceTitle || '',
                sourceUrl: story.sourceUrl || '',
                thumbUrl: story.thumbUrl || '',
                tags: story.tags || [],
                author: story.author || '',
                textContent: story.text?.text || '',
                isReadyToServe: story.isReadyToServe || false
            });
        }
    }, [story]);

    // Effect to validate and update language selection when languages are loaded
    useEffect(() => {
        if (languages.length > 0 && formData.language) {
            // Check if the current language value exists in the loaded languages
            const languageExists = languages.some(lang => lang._id === formData.language);
            if (!languageExists) {
                // If the language doesn't exist, reset to empty string
                setFormData(prev => ({
                    ...prev,
                    language: ''
                }));
            }
        }
    }, [languages, formData.language]);

    // Function to generate tags from title and author
    const generateTagsFromTitleAndAuthor = () => {
        const newTags = ['short story', 'short'];
        
        // Extract words from title
        if (formData.title.trim()) {
            const titleWords = formData.title
                .trim()
                .toLowerCase()
                .split(/\s+/)
                .filter(word => word.length > 1) // Only words longer than 1 character
                .map(word => word.replace(/[^\w]/g, '')) // Remove punctuation
                .filter(word => word.length > 1); // Filter again after cleaning
            
            newTags.push(...titleWords);
        }
        
        // Extract words from author
        if (formData.author.trim()) {
            const authorWords = formData.author
                .trim()
                .toLowerCase()
                .split(/\s+/)
                .filter(word => word.length > 1) // Only words longer than 1 character
                .map(word => word.replace(/[^\w]/g, '')) // Remove punctuation
                .filter(word => word.length > 1); // Filter again after cleaning
            
            newTags.push(...authorWords);
        }
        
        // Combine with existing tags and remove duplicates (case-insensitive)
        const allTags = [...formData.tags];
        newTags.forEach(newTag => {
            const tagLower = newTag.toLowerCase();
            const existsAlready = allTags.some(existingTag => 
                existingTag.toLowerCase() === tagLower
            );
            
            if (!existsAlready && newTag.length > 1) {
                allTags.push(newTag);
            }
        });
        
        if (isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                tags: allTags
            }));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
            
            // Clear error when user starts typing
            if (errors[name]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }
        }
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim()) && isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, tagInput.trim()]
            }));
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        if (isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                tags: prev.tags.filter(tag => tag !== tagToRemove)
            }));
        }
    };

    const handleTagInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.title.trim()) {
            newErrors.title = 'Title is required';
        }

        if (!formData.language) {
            newErrors.language = 'Language is required';
        }

        if (!formData.level) {
            newErrors.level = 'Learner level is required';
        }

        if (!formData.author.trim()) {
            newErrors.author = 'Author is required';
        }

        if (!formData.textContent.trim()) {
            newErrors.textContent = 'Story content is required';
        }

        if (formData.tags.length === 0) {
            newErrors.tags = 'At least one tag is required';
        }

        if (isMountedRef.current) {
            setErrors(newErrors);
        }
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        if (isMountedRef.current) {
            setLoading(true);
        }
        
        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const handleCheckTranslations = async () => {
        if (!story?.text?._id) {
            alert('No text ID available for translation check');
            return;
        }

        setMonitoringDialog({ open: true, loading: true, data: null });

        try {
            const response = await userRequest.get(`/text/check-translations/${story.text._id}`);
            setMonitoringDialog({ open: true, loading: false, data: response.data });
        } catch (error) {
            console.error('Error checking translations:', error);
            
            // Handle 404 (no translation found) specifically
            if (error.response?.status === 404) {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'No translation found for this story. You need to create a translation first.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: [],
                        noTranslation: true
                    } 
                });
            } else {
                setMonitoringDialog({ 
                    open: true, 
                    loading: false, 
                    data: { 
                        error: 'Failed to check translations. Please try again.',
                        hasUntranslated: false,
                        untranslatedWords: [],
                        untranslatedPhrases: []
                    } 
                });
            }
        }
    };

    const handleCloseMonitoringDialog = () => {
        setMonitoringDialog({ open: false, loading: false, data: null });
    };

    const handleReadyToServeToggle = () => {
        if (!formData.isReadyToServe) {
            // User is trying to set to ready - show confirmation dialog
            setReadyToServeDialog({ open: true, hasChecked: false });
        } else {
            // User is turning it off - allow immediately
            if (isMountedRef.current) {
                setFormData(prev => ({
                    ...prev,
                    isReadyToServe: false
                }));
            }
        }
    };

    const handleReadyToServeConfirm = (confirmed) => {
        setReadyToServeDialog({ open: false, hasChecked: false });
        if (confirmed && isMountedRef.current) {
            setFormData(prev => ({
                ...prev,
                isReadyToServe: true
            }));
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        error={!!errors.title}
                        helperText={errors.title}
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Author"
                        name="author"
                        value={formData.author}
                        onChange={handleInputChange}
                        error={!!errors.author}
                        helperText={errors.author}
                        required
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={!!errors.language}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            name="language"
                            value={
                                languages.length === 0 
                                    ? '' // If no languages loaded yet, use empty string
                                    : languages.some(lang => lang._id === formData.language) 
                                        ? formData.language // If valid language, use it
                                        : '' // If invalid language, use empty string
                            }
                            onChange={handleInputChange}
                            label="Language"
                            required
                            disabled={languages.length === 0}
                        >
                            {languages.length === 0 ? (
                                <MenuItem value="" disabled>
                                    Loading languages...
                                </MenuItem>
                            ) : (
                                languages.map((language) => (
                                    <MenuItem key={language._id} value={language._id}>
                                        {language.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                        {errors.language && (
                            <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
                                {errors.language}
                            </Typography>
                        )}
                        {languages.length === 0 && (
                            <Typography variant="caption" color="info" sx={{ ml: 2, mt: 0.5 }}>
                                Loading available languages...
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <LevelSelector
                        value={formData.level}
                        onChange={(e) => handleInputChange({ target: { name: 'level', value: e.target.value } })}
                        error={errors.level}
                        required
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Source Title"
                        name="sourceTitle"
                        value={formData.sourceTitle}
                        onChange={handleInputChange}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Source URL"
                        name="sourceUrl"
                        value={formData.sourceUrl}
                        onChange={handleInputChange}
                        type="url"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Thumbnail URL"
                        name="thumbUrl"
                        value={formData.thumbUrl}
                        onChange={handleInputChange}
                        type="url"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Publication Settings
                        </Typography>
                        
                        {/* Check Translations Button */}
                        {story?.text?._id && (
                            <Box mb={2}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<FindInPage />}
                                    onClick={handleCheckTranslations}
                                    size="small"
                                    disabled={!story?.text?._id}
                                >
                                    Check Missing Translations
                                </Button>
                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Check this before marking as "Ready to Serve" to ensure all translations are complete
                                </Typography>
                            </Box>
                        )}

                        {/* Ready to Serve Toggle */}
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isReadyToServe}
                                    onChange={handleReadyToServeToggle}
                                    name="isReadyToServe"
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        Ready to Serve
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formData.isReadyToServe 
                                            ? "This story is ready for users to view" 
                                            : "This story is not ready for users to view"
                                        }
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </Grid>

                {/* Story Content - Main Text Area */}
                <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                        Story Content *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={12}
                        label="Enter your story content here..."
                        name="textContent"
                        value={formData.textContent}
                        onChange={handleInputChange}
                        error={!!errors.textContent}
                        helperText={errors.textContent || 'Write the complete story content in this field'}
                        required
                        sx={{
                            '& .MuiInputBase-root': {
                                fontSize: '1.1rem',
                                lineHeight: 1.6
                            }
                        }}
                        placeholder="Once upon a time..."
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="subtitle1">
                            Tags {errors.tags && <span style={{ color: 'red', fontSize: '0.8rem' }}>({errors.tags})</span>}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoAwesome />}
                            onClick={generateTagsFromTitleAndAuthor}
                            disabled={!formData.title.trim() && !formData.author.trim()}
                        >
                            Auto-generate from Title & Author
                        </Button>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <TextField
                            label="Add tag"
                            value={tagInput}
                            onChange={(e) => {
                                if (isMountedRef.current) {
                                    setTagInput(e.target.value);
                                }
                            }}
                            onKeyPress={handleTagInputKeyPress}
                            size="small"
                            sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                            onClick={handleAddTag}
                            disabled={!tagInput.trim()}
                            color="primary"
                        >
                            <Add />
                        </IconButton>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {formData.tags.map((tag, index) => (
                            <Chip
                                key={index}
                                label={tag}
                                onDelete={() => handleRemoveTag(tag)}
                                deleteIcon={<Delete />}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                </Grid>

                {formData.thumbUrl && (
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                            Thumbnail Preview:
                        </Typography>
                        <img 
                            src={formData.thumbUrl} 
                            alt="Thumbnail preview" 
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </Grid>
                )}
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <Button onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button 
                    type="submit" 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                >
                    {loading ? 'Saving...' : (story ? 'Update' : 'Create')}
                </Button>
            </Box>

            {/* Translation Monitoring Dialog */}
            <TranslationMonitoringDialog
                open={monitoringDialog.open}
                onClose={handleCloseMonitoringDialog}
                loading={monitoringDialog.loading}
                data={monitoringDialog.data}
            />

            {/* Ready to Serve Confirmation Dialog */}
            <Dialog
                open={readyToServeDialog.open}
                onClose={() => handleReadyToServeConfirm(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Warning color="warning" />
                        Confirm Ready to Serve
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Before marking this story as "Ready to Serve", please ensure you have:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, mt: 2 }}>
                        <li>
                            <Typography variant="body2">
                                Checked for missing translations using the "Check Missing Translations" button
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Reviewed the story content for quality and completeness
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="body2">
                                Verified all metadata (title, author, tags, etc.) is correct
                            </Typography>
                        </li>
                    </Box>
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
                        Have you completed these checks and is the story ready for users?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleReadyToServeConfirm(false)}>
                        No, I need to check more
                    </Button>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        onClick={() => handleReadyToServeConfirm(true)}
                    >
                        Yes, it is ready
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShortStoryForm; 